import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, LogOut, Plus, Truck, Trash2, ChevronDown, PackagePlus,
} from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { key: "booking_confirmed", label: "Booking confirmed" },
  { key: "collection_scheduled", label: "Collection scheduled" },
  { key: "collected", label: "Collected" },
  { key: "in_transit", label: "In transit" },
  { key: "out_for_delivery", label: "Out for delivery" },
  { key: "delivered", label: "Delivered" },
];
const stageLabel = (key) => STAGES.find((s) => s.key === key)?.label || key;

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-blue-600 focus:ring-2 focus:ring-blue-100";

const Admin = () => {
  const [user, setUser] = useState(null);
  const [shipments, setShipments] = useState(null);
  const [form, setForm] = useState({ vehicle: "", collection: "", delivery: "", eta: "" });
  const [creating, setCreating] = useState(false);
  const [openRef, setOpenRef] = useState(null);
  const [edit, setEdit] = useState({ status: "", note: "", eta: "" });
  const navigate = useNavigate();

  const load = async () => {
    const res = await axios.get(`${API}/admin/shipments`, { withCredentials: true });
    setShipments(res.data);
  };

  useEffect(() => {
    (async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(true);
        await load();
      } catch {
        navigate("/admin/login");
      }
    })();
  }, [navigate]);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    navigate("/admin/login");
  };

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/admin/shipments`,
        { ...form, eta: form.eta || null },
        { withCredentials: true }
      );
      toast.success(`Shipment ${res.data.reference} created`);
      setForm({ vehicle: "", collection: "", delivery: "", eta: "" });
      await load();
    } catch {
      toast.error("Could not create the shipment. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const update = async (reference) => {
    try {
      await axios.patch(
        `${API}/admin/shipments/${reference}`,
        { status: edit.status || undefined, note: edit.note || null, eta: edit.eta || null },
        { withCredentials: true }
      );
      toast.success(`${reference} updated`);
      setOpenRef(null);
      await load();
    } catch {
      toast.error("Update failed. Please try again.");
    }
  };

  const remove = async (reference) => {
    if (!window.confirm(`Delete ${reference}? Customers will no longer be able to track it.`)) return;
    try {
      await axios.delete(`${API}/admin/shipments/${reference}`, { withCredentials: true });
      toast.success(`${reference} deleted`);
      await load();
    } catch {
      toast.error("Delete failed. Please try again.");
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-700" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-5 py-12 lg:px-8" data-testid="admin-page">
      <Seo title="Shipment Control | Solaris Transport LTD" description="Manage shipments and tracking statuses." path="/admin" />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-blue-700">Operations</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-900">Shipment control</h1>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700 transition-colors hover:border-red-300 hover:text-red-600"
          data-testid="admin-logout-button"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>

      {/* Create */}
      <form onSubmit={create} className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="shipment-create-form">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
          <PackagePlus className="h-5 w-5 text-blue-700" /> New shipment
        </h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <input required value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} placeholder="Vehicle, e.g. Range Rover Evoque" className={inputCls} data-testid="shipment-vehicle-input" />
          <input value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })} type="date" className={inputCls} data-testid="shipment-eta-input" />
          <input required value={form.collection} onChange={(e) => setForm({ ...form, collection: e.target.value })} placeholder="Collection — City or Postcode" className={inputCls} data-testid="shipment-collection-input" />
          <input required value={form.delivery} onChange={(e) => setForm({ ...form, delivery: e.target.value })} placeholder="Delivery — City or Postcode" className={inputCls} data-testid="shipment-delivery-input" />
        </div>
        <button
          type="submit" disabled={creating}
          className="mt-5 flex items-center gap-2 rounded-full bg-blue-700 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-800 disabled:opacity-60"
          data-testid="shipment-create-submit"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Create tracking reference
        </button>
        <p className="mt-3 text-xs text-slate-400">A reference like STR-2026-0482 is generated automatically — give it to the customer with their booking confirmation.</p>
      </form>

      {/* List */}
      <div className="mt-8 space-y-4" data-testid="shipments-list">
        {shipments === null ? (
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-700" />
        ) : shipments.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-10 text-center text-sm text-slate-500">No shipments yet.</p>
        ) : (
          shipments.map((s) => (
            <div key={s.reference} className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`shipment-card-${s.reference}`}>
              <button
                onClick={() => {
                  setOpenRef(openRef === s.reference ? null : s.reference);
                  setEdit({ status: s.status, note: "", eta: s.eta || "" });
                }}
                className="flex w-full flex-wrap items-center justify-between gap-3 p-5 text-left"
                data-testid={`shipment-toggle-${s.reference}`}
              >
                <div className="flex items-center gap-4">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${s.status === "delivered" ? "bg-emerald-50" : "bg-blue-50"}`}>
                    <Truck className={`h-5 w-5 ${s.status === "delivered" ? "text-emerald-600" : "text-blue-700"}`} />
                  </span>
                  <div>
                    <p className="font-mono text-sm font-semibold text-slate-900">{s.reference}</p>
                    <p className="text-sm text-slate-500">{s.vehicle} · {s.collection} → {s.delivery}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {s.demo && <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">Demo</span>}
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${s.status === "delivered" ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`}>
                    {stageLabel(s.status)}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${openRef === s.reference ? "rotate-180" : ""}`} />
                </div>
              </button>

              {openRef === s.reference && (
                <div className="border-t border-slate-100 bg-slate-50 p-5" data-testid={`shipment-editor-${s.reference}`}>
                  <div className="grid gap-4 sm:grid-cols-3">
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Status</span>
                      <select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} className={inputCls} data-testid={`status-select-${s.reference}`}>
                        {STAGES.map((st) => <option key={st.key} value={st.key}>{st.label}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">ETA (optional)</span>
                      <input type="date" value={edit.eta} onChange={(e) => setEdit({ ...edit, eta: e.target.value })} className={inputCls} data-testid={`eta-input-${s.reference}`} />
                    </label>
                    <label className="block sm:col-span-1">
                      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Note for timeline</span>
                      <input value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} placeholder="e.g. Collected, photos taken" className={inputCls} data-testid={`status-note-${s.reference}`} />
                    </label>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <button onClick={() => update(s.reference)} className="rounded-full bg-blue-700 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-blue-800" data-testid={`status-update-${s.reference}`}>
                      Save update
                    </button>
                    <button onClick={() => remove(s.reference)} className="flex items-center gap-2 rounded-full border border-red-200 px-5 py-2.5 text-sm font-bold text-red-600 transition-colors hover:bg-red-50" data-testid={`shipment-delete-${s.reference}`}>
                      <Trash2 className="h-4 w-4" /> Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Admin;
